import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

function EditWorkshop() {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [workshop, setWorkshop] = useState({
    title: '',
    shortDescription: '',
    description: '',
    startDate: '',
    endDate: '',
    location: '',
    maxParticipants: 12,
    singleOccupancy: 0,
    doubleOccupancy: 0,
    included: [''],
    itinerary: [{ day: 1, title: '', description: '' }],
    gearList: [''],
    cancellationPolicy: '',
    imageUrl: '',
    status: 'draft'
  });

  useEffect(() => {
    fetchWorkshop();
  }, [id]);

  const fetchWorkshop = async () => {
    try {
      const workshopDoc = await getDoc(doc(db, 'sessions_workshops', id));
      
      if (!workshopDoc.exists()) {
        setError('Workshop not found');
        return;
      }

      const workshopData = workshopDoc.data();
      
      // Check if current user owns this workshop
      if (workshopData.instructorId !== currentUser.uid) {
        setError('You are not authorized to edit this workshop');
        return;
      }

      // Format dates for input fields
      const formattedData = {
        ...workshopData,
        startDate: workshopData.startDate ? workshopData.startDate.split('T')[0] : '',
        endDate: workshopData.endDate ? workshopData.endDate.split('T')[0] : '',
        included: workshopData.included?.length > 0 ? workshopData.included : [''],
        gearList: workshopData.gearList?.length > 0 ? workshopData.gearList : [''],
        itinerary: workshopData.itinerary?.length > 0 ? workshopData.itinerary : [{ day: 1, title: '', description: '' }]
      };

      setWorkshop(formattedData);
    } catch (error) {
      console.error('Error fetching workshop:', error);
      setError('Error loading workshop');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setWorkshop(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    setWorkshop(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field, defaultValue = '') => {
    setWorkshop(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const removeArrayItem = (field, index) => {
    setWorkshop(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const addItineraryDay = () => {
    setWorkshop(prev => ({
      ...prev,
      itinerary: [...prev.itinerary, { 
        day: prev.itinerary.length + 1, 
        title: '', 
        description: '' 
      }]
    }));
  };

  const handleItineraryChange = (index, field, value) => {
    setWorkshop(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      // Clean up empty items and prepare update data
      const updateData = {
        ...workshop,
        included: workshop.included.filter(item => item.trim() !== ''),
        gearList: workshop.gearList.filter(item => item.trim() !== ''),
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'sessions_workshops', id), updateData);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating workshop:', error);
      setError('Error updating workshop. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-900">Loading workshop...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg p-8 border border-gray-300">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {error}
            </h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Edit Workshop</h1>
          <p className="text-gray-600 mt-2">
            Update your workshop details
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-lg p-6 border border-gray-300">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workshop Title
                </label>
                <input
                  type="text"
                  value={workshop.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={workshop.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={workshop.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={workshop.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Participants
                </label>
                <input
                  type="number"
                  value={workshop.maxParticipants}
                  onChange={(e) => handleInputChange('maxParticipants', parseInt(e.target.value) || 12)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="1"
                  max="50"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-lg p-6 border border-gray-300">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Description</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Short Description
                </label>
                <input
                  type="text"
                  value={workshop.shortDescription}
                  onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief overview of the workshop"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Description
                </label>
                <textarea
                  value={workshop.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Detailed description of the workshop..."
                />
              </div>
            </div>
          </div>

          {/* What's Included */}
          <div className="bg-white rounded-lg p-6 border border-gray-300">
            <h2 className="text-xl font-bold text-gray-900 mb-6">What's Included</h2>
            
            <div className="space-y-4">
              {workshop.included.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleArrayChange('included', index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Professional instruction, meals, transportation"
                  />
                  {workshop.included.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('included', index)}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('included', '')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add Item
              </button>
            </div>
          </div>

          {/* Itinerary */}
          <div className="bg-white rounded-lg p-6 border border-gray-300">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Itinerary</h2>
            
            <div className="space-y-6">
              {workshop.itinerary.map((day, index) => (
                <div key={index} className="border border-gray-300 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Day {day.day}</h3>
                    {workshop.itinerary.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          setWorkshop(prev => ({
                            ...prev,
                            itinerary: prev.itinerary.filter((_, i) => i !== index)
                          }));
                        }}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Remove Day
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Day Title
                      </label>
                      <input
                        type="text"
                        value={day.title}
                        onChange={(e) => handleItineraryChange(index, 'title', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Introduction and Camera Basics"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Day Description
                      </label>
                      <textarea
                        value={day.description}
                        onChange={(e) => handleItineraryChange(index, 'description', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Describe what will happen on this day..."
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addItineraryDay}
                className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700"
              >
                Add Day
              </button>
            </div>
          </div>

          {/* Gear List */}
          <div className="bg-white rounded-lg p-6 border border-gray-300">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Required Gear</h2>
            
            <div className="space-y-4">
              {workshop.gearList.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleArrayChange('gearList', index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., DSLR camera, tripod, memory cards"
                  />
                  {workshop.gearList.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('gearList', index)}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('gearList', '')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add Item
              </button>
            </div>
          </div>

          {/* Cancellation Policy */}
          <div className="bg-white rounded-lg p-6 border border-gray-300">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Cancellation Policy</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Policy Details
              </label>
              <textarea
                value={workshop.cancellationPolicy}
                onChange={(e) => handleInputChange('cancellationPolicy', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Explain your cancellation and refund policy..."
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditWorkshop; 